import { useEffect } from "react";

const DEADZONE = 0.55;
const MOVE_REPEAT_MS = 190;

export function GamepadNavigation() {
  useEffect(() => {
    let raf = 0;
    let lastMoveAt = 0;
    let acceptHeld = false;
    let backHeld = false;

    const tick = () => {
      raf = requestAnimationFrame(tick);
      const pad = firstPad();
      if (!pad) return;

      const canNavigate = shouldNavigateUi();
      const accept = buttonDown(pad, 0);
      const back = buttonDown(pad, 1);
      if (canNavigate) {
        ensureFocus();
        const x = axisWithDpad(pad, "x");
        const y = axisWithDpad(pad, "y");
        const now = performance.now();
        if (now - lastMoveAt > MOVE_REPEAT_MS) {
          if (Math.abs(x) > Math.abs(y) && Math.abs(x) > DEADZONE) {
            moveFocus(x > 0 ? "right" : "left");
            lastMoveAt = now;
          } else if (Math.abs(y) > DEADZONE) {
            moveFocus(y > 0 ? "down" : "up");
            lastMoveAt = now;
          }
        }
        if (accept && !acceptHeld) activateFocused();
        if (back && !backHeld) activateBack();
      }
      acceptHeld = accept;
      backHeld = back;
    };
    raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
  }, []);

  return null;
}

function shouldNavigateUi() {
  const inGame = /^\/(play\/lesson|battle\/play)/.test(window.location.pathname);
  if (!inGame) return true;
  return !!document.querySelector(".challenge-overlay, .pause-overlay, .teach-overlay, .results-overlay");
}

function firstPad() {
  return Array.from(navigator.getGamepads?.() ?? []).find((p): p is Gamepad => !!p && p.connected);
}

function axisWithDpad(pad: Gamepad, axis: "x" | "y") {
  if (axis === "x") {
    const dpad = (buttonDown(pad, 15) ? 1 : 0) - (buttonDown(pad, 14) ? 1 : 0);
    return dpad || (pad.axes[0] ?? 0);
  }
  const dpad = (buttonDown(pad, 13) ? 1 : 0) - (buttonDown(pad, 12) ? 1 : 0);
  return dpad || (pad.axes[1] ?? 0);
}

function buttonDown(pad: Gamepad, index: number) {
  const b = pad.buttons[index];
  return !!b && (b.pressed || b.value > 0.5);
}

function focusables() {
  return Array.from(
    document.querySelectorAll<HTMLElement>(
      "button:not(:disabled), a[href], input:not(:disabled), select:not(:disabled), textarea:not(:disabled), [tabindex]:not([tabindex='-1'])",
    ),
  ).filter((el) => {
    const r = el.getBoundingClientRect();
    const style = getComputedStyle(el);
    return r.width > 0 && r.height > 0 && style.visibility !== "hidden" && style.display !== "none";
  });
}

function ensureFocus() {
  const active = document.activeElement;
  if (active instanceof HTMLElement && focusables().includes(active)) return;
  focusables()[0]?.focus();
}

function moveFocus(dir: "left" | "right" | "up" | "down") {
  const items = focusables();
  if (items.length === 0) return;
  const active = document.activeElement instanceof HTMLElement ? document.activeElement : items[0]!;
  if (!items.includes(active)) {
    items[0]!.focus();
    return;
  }
  const a = center(active);
  let best: HTMLElement | null = null;
  let bestScore = Infinity;
  for (const item of items) {
    if (item === active) continue;
    const b = center(item);
    const dx = b.x - a.x;
    const dy = b.y - a.y;
    if (dir === "left" && dx >= -4) continue;
    if (dir === "right" && dx <= 4) continue;
    if (dir === "up" && dy >= -4) continue;
    if (dir === "down" && dy <= 4) continue;
    const primary = dir === "left" || dir === "right" ? Math.abs(dx) : Math.abs(dy);
    const secondary = dir === "left" || dir === "right" ? Math.abs(dy) : Math.abs(dx);
    const score = primary + secondary * 2.2;
    if (score < bestScore) {
      bestScore = score;
      best = item;
    }
  }
  (best ?? wrapFocus(items, active, dir))?.focus();
}

function wrapFocus(items: HTMLElement[], active: HTMLElement, dir: "left" | "right" | "up" | "down") {
  const sorted = [...items].sort((a, b) => {
    const ar = a.getBoundingClientRect();
    const br = b.getBoundingClientRect();
    return ar.top - br.top || ar.left - br.left;
  });
  const index = sorted.indexOf(active);
  if (dir === "left" || dir === "up") return sorted[(index - 1 + sorted.length) % sorted.length];
  return sorted[(index + 1) % sorted.length];
}

function center(el: HTMLElement) {
  const r = el.getBoundingClientRect();
  return { x: r.left + r.width / 2, y: r.top + r.height / 2 };
}

function activateFocused() {
  const active = document.activeElement;
  if (!(active instanceof HTMLElement)) return;
  active.click();
}

function activateBack() {
  const close = document.querySelector<HTMLElement>(
    ".home-mode-close, .lesson-intro-back, .results-home-link, .pause-btn-exit, [aria-label='Close'], [aria-label='Menu']",
  );
  if (close) close.click();
}
